import { useEffect, useState } from "react";
import { Clock, Map, MapPin, MessageCircle, Navigation, Ticket } from "lucide-react";
import { useLang } from "./LangContext";
import { useSettings, has, whatsappBookingLink } from "./SettingsContext";
import { ChapterHead, FadeUp, Soon } from "./Reveal";
import { HourlyPrice } from "./HourlyPrice";
import { PriceItems } from "./PriceItems";

export default function Branches() {
  const { t } = useLang();
  const { settings } = useSettings();
  const b = t.branches;
  const [hi, setHi] = useState(null);

  useEffect(() => {
    let timer;
    const handler = ({ detail }) => {
      setHi(detail);
      clearTimeout(timer);
      timer = setTimeout(() => setHi(null), 2800);
    };
    window.addEventListener("mdr:highlight-branch", handler);
    return () => {
      window.removeEventListener("mdr:highlight-branch", handler);
      clearTimeout(timer);
    };
  }, []);

  return (
    <section id="subeler" className="section" data-testid="branches-section">
      <div className="wrap">
        <ChapterHead num={b.num} label={b.label} title={b.title} lead={b.lead} />
        <div className="branch-grid">
          {b.items.map((branch, i) => {
            const hours = settings?.hours?.[branch.id];
            const waHref = whatsappBookingLink(branch, settings?.contact, b.waMessage);
            const ticket = settings?.tickets?.[branch.id];
            return (
            <FadeUp key={branch.id} delay={i * 0.1} className="branch-cell">
              <div
                className={`branch-card ${hi === branch.id ? "is-hi" : ""}`}
                data-testid={`branch-card-${branch.id}`}
              >
                <span className="branch-idx" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="branch-city">{branch.city}</p>
                <h3>{branch.name}</h3>
                <p className="branch-addr" data-testid={`branch-address-${branch.id}`}>
                  <MapPin size={15} aria-hidden="true" />
                  <span>
                    {branch.lines.map((line) => (
                      <span key={line}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </span>
                </p>
                {has(hours) && (
                  <p className="branch-hours" data-testid={`branch-hours-${branch.id}`}>
                    <Clock size={14} aria-hidden="true" />
                    {hours}
                  </p>
                )}
                <div className="branch-pricing" data-testid={`branch-pricing-${branch.id}`}>
                  <span className="price-label">{t.pricing.title} · {t.pricing.pc}</span>
                  <HourlyPrice value={settings?.prices?.[branch.id]} testid={`branch-price-${branch.id}`} />
                </div>
                <PriceItems items={settings?.price_items?.[branch.id]} testid={`branch-price-items-${branch.id}`} />
                {ticket?.enabled && has(ticket.title) && (
                  <div className="branch-ticket" data-testid={`branch-ticket-${branch.id}`}>
                    <span className="branch-ticket-notch" aria-hidden="true" />
                    <span className="branch-ticket-notch is-right" aria-hidden="true" />
                    <div className="branch-ticket-icon" aria-hidden="true">
                      <Ticket size={20} />
                    </div>
                    <div className="branch-ticket-body">
                      <span className="branch-ticket-tag">{b.ticketTag}</span>
                      <p className="branch-ticket-title font-display" data-testid={`branch-ticket-title-${branch.id}`}>{ticket.title}</p>
                      {has(ticket.text) && <p className="branch-ticket-text" data-testid={`branch-ticket-text-${branch.id}`}>{ticket.text}</p>}
                    </div>
                  </div>
                )}
                {branch.photo ? (
                  <div className="branch-photo" data-testid={`branch-photo-${branch.id}`}>
                    <img src={branch.photo} alt={branch.photoAlt || branch.name} loading="lazy" decoding="async" />
                  </div>
                ) : (
                  <div className="map-slot" data-testid={`branch-map-slot-${branch.id}`}>
                    <Map size={26} aria-hidden="true" />
                    <Soon text={b.mapSoon} testid={`branch-map-soon-${branch.id}`} />
                  </div>
                )}
                <a
                  className="btn btn-primary maps-btn"
                  href={branch.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`branch-maps-button-${branch.id}`}
                >
                  <Navigation size={15} aria-hidden="true" />
                  {b.mapsBtn}
                </a>
                {waHref && (
                  <a
                    className="btn btn-whatsapp"
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`branch-whatsapp-booking-${branch.id}`}
                  >
                    <MessageCircle size={15} aria-hidden="true" />
                    {b.waBtn}
                  </a>
                )}
              </div>
            </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}
